import { ApolloError } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { Button, Field, FluentProvider, Textarea, webLightTheme } from '@fluentui/react-components';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { CoherencePanelStyles } from '../../app/common/styles/CommonStyleObjects';

import ActionButtons from '../common/ActionButtons';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import RejectButtonMode from './RejectButtonMode';

interface IRejectButtonProps {
  disabled?: boolean;
  onClick: (reason?: string, callback?: () => void) => void;
  mode: RejectButtonMode;
  mutationLoading: boolean;
  mutationError: ApolloError;
  onRejectOverrideMethod?: () => void;
}

interface IRejectReason {
  reason?: string;
}

const validateReason = (reason: string): string => {
  if (reason.trim().replace(/\s\s+/g, ' ').length < 1) {
    return 'Reason cannot be blank';
  }
  return null;
};

const RejectButton = ({
  disabled,
  onClick,
  mode,
  mutationLoading,
  mutationError,
  onRejectOverrideMethod,
}: IRejectButtonProps): JSX.Element => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { handleSubmit, control, errors } = useForm();

  const onRequestChangesClick = (): void => {
    if (onRejectOverrideMethod) {
      onRejectOverrideMethod();
    } else {
      setIsPanelOpen(true);
    }
  };

  const closePanel = (): void => {
    setIsPanelOpen(false);
  };

  const onSubmit = ({ reason }: IRejectReason) => {
    onClick(reason, closePanel);
  };

  const buttonStyles = {
    height: '32px',
    lineHeight: '1',
  };

  const displayLabel = mode === RejectButtonMode.Reject ? 'Reject Request' : 'Request Changes';
  const commentLabel = mode === RejectButtonMode.Reject ? 'Rejecting' : 'Requesting Changes';
  const buttonLabel = mode === RejectButtonMode.Reject ? 'Reject' : 'Request Changes';

  return (
    <>
      <Button disabled={disabled} onClick={onRequestChangesClick} style={buttonStyles}>
        {buttonLabel}
      </Button>
      <CoherencePanel
        panelSize={CoherencePanelSize.medium}
        titleText={displayLabel}
        isOpen={isPanelOpen}
        onDismiss={closePanel}
        styles={CoherencePanelStyles}
        hasCloseButton
        closeButtonAriaLabel={`Close ${displayLabel}`}
        onRenderFooter={(): JSX.Element => (
          <ActionButtons
            mutationLoading={mutationLoading}
            closePanel={closePanel}
            handleSubmit={handleSubmit(onSubmit)}
            saveLabel={displayLabel}
            saveTitle={displayLabel}
            cancelLabel={`Cancel ${displayLabel}`}
            cancelTitle={`Cancel ${displayLabel}`}
          />
        )}
      >
        <FluentProvider theme={webLightTheme}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <LoadingErrorMessage loading={mutationLoading} error={mutationError} />
            <Controller
              control={control}
              name="reason"
              defaultValue=""
              rules={{
                required: 'A reason is required',
                validate: validateReason,
              }}
              render={({ onChange, onBlur, value, name, ref }) => (
                <Field
                  label={`Reason For ${commentLabel}`}
                  required
                  validationMessage={errors?.reason?.message}
                >
                  <Textarea
                    value={value}
                    name={name}
                    ref={ref}
                    onBlur={onBlur}
                    onChange={(ev, textareaData) => onChange(textareaData.value)}
                  />
                </Field>
              )}
            />
          </form>
        </FluentProvider>
      </CoherencePanel>
    </>
  );
};

export default RejectButton;
